import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Car, Plus } from "lucide-react";
import { api, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { VEHICLE_DELETE_NOTE, belgeTarih } from "../lib/labels";
import VehicleCard from "../components/VehicleCard";
import { VehicleFormModal, GarageRecordModal, TransferModal } from "../components/VehicleModals";

const EMPTY_BELGELER = { sigorta: { baslangic: "", bitis: "" }, kasko: { baslangic: "", bitis: "" }, muayene: { baslangic: "", bitis: "" } };
const EMPTY_VEHICLE = { vin: "", plate: "", brand: "", model: "", year: new Date().getFullYear(), km: 0, engine: "", fuel: "", transmission: "", color: "", body_type: "", trim: "", vehicle_type: "Otomobil", photo: "", ruhsat: "", ocr_vin: "", belgeler: EMPTY_BELGELER, photos: [] };
const EMPTY_RECORD = { type: "servis", date: new Date().toISOString().slice(0, 10), title: "", description: "", km: "", cost: "", severity: "low" };

export default function MyVehicles() {
  const { user } = useAuth();
  const isBusiness = !!user && (user.role === "servis" || user.role === "cekici");
  const [vehicles, setVehicles] = useState(null);
  const [vehicleForm, setVehicleForm] = useState(null); // {mode:'add'|'edit', data, id?}
  const [recordForm, setRecordForm] = useState(null); // {vehicleId, record|null, data}
  const [transferFor, setTransferFor] = useState(null);
  const [transferEmail, setTransferEmail] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(
    () => api.get("/my-vehicles").then((r) => setVehicles(r.data)).catch(() => setVehicles([])),
    []);
  useEffect(() => { load(); }, [load]);

  const sortedHistories = useMemo(() => {
    const m = {};
    (vehicles || []).forEach((v) => {
      m[v.id] = [...(v.history || [])].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    });
    return m;
  }, [vehicles]);

  const sortedVehicles = useMemo(
    () => [...(vehicles || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")),
    [vehicles]);

  const uploadPhotos = async (files) => {
    setUploading(true);
    const paths = [];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        paths.push(data.path);
      }
      toast.success(`${paths.length} fotoğraf yüklendi`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
    return paths;
  };

  const changePhoto = async (v, file) => {
    if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error("Sadece JPG veya PNG yükleyebilirsiniz"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Fotoğraf 5 MB'ı aşamaz"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd);
      await api.put(`/my-vehicles/${v.id}/photo`, { photo: data.path });
      toast.success("Araç fotoğrafı güncellendi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitVehicle = async (e) => {
    e.preventDefault();
    const d = vehicleForm.data;
    if (vehicleForm.mode === "add" && !(d.ruhsat || "").trim() && user?.role !== "admin") {
      toast.error("Ruhsat fotoğrafı yüklenmesi zorunludur (şasi doğrulaması için).");
      return;
    }
    const payload = { ...d, year: Number(d.year), km: Number(d.km) || 0 };
    try {
      if (vehicleForm.mode === "add") {
        await api.post("/my-vehicles", payload);
        toast.success("Araç başarıyla eklendi");
      } else {
        await api.put(`/my-vehicles/${vehicleForm.id}`, payload);
        toast.success("Araç güncellendi");
      }
      setVehicleForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteVehicle = async (v) => {
    if (!window.confirm(`${v.brand} ${v.model} (${v.vin}) silinsin mi?`)) return;
    try {
      await api.delete(`/my-vehicles/${v.id}`);
      toast.success("Araç silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitRecord = async (e) => {
    e.preventDefault();
    const d = recordForm.data;
    const payload = { ...d, km: d.km === "" ? null : Number(d.km), cost: d.cost === "" ? null : Number(d.cost), severity: d.severity || null };
    try {
      if (recordForm.record) {
        await api.put(`/my-vehicles/${recordForm.vehicleId}/records/${recordForm.record.id}`, payload);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post(`/my-vehicles/${recordForm.vehicleId}/records`, payload);
        toast.success("Kayıt eklendi");
      }
      setRecordForm(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteRecord = async (vid, rid) => {
    if (!window.confirm("Bu kayıt silinsin mi?")) return;
    try {
      await api.delete(`/my-vehicles/${vid}/records/${rid}`);
      toast.success("Kayıt silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!window.confirm("Araç kaydı bu kullanıcıya devredilecek ve listenizden kaldırılacak. Onaylıyor musunuz?")) return;
    try {
      const { data } = await api.post(`/my-vehicles/${transferFor.id}/transfer`, { email: transferEmail });
      if (data.email_sent === false) {
        toast.warning(`Devir daveti oluşturuldu ancak davet e-postası gönderilemedi: ${data.email_error || "bilinmeyen hata"}`);
      } else {
        toast.success("Devir daveti gönderildi; araç, karşı taraf kabul edince devredilecek");
      }
      setTransferFor(null);
      setTransferEmail("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Car className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Araçlarım</h1>
          </div>
          {!isBusiness && (
          <button data-testid="garage-add-vehicle-button"
            onClick={() => setVehicleForm({ mode: "add", data: { ...EMPTY_VEHICLE } })}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.4)] transition-all text-sm">
            <Plus className="w-4 h-4" /> Araç Ekle
          </button>
          )}
        </div>
        <p className="mt-2 text-sm text-slate-400">Araçlarınızı kaydedin; bakım, kaza, boya ve ekspertiz kayıtlarını dijital künyede tutun.</p>
        <p data-testid="vehicles-delete-note" className="mt-2 text-xs text-slate-500">{VEHICLE_DELETE_NOTE}</p>

        {vehicles === null ? (
          <div className="mt-16 flex justify-center">
            <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : vehicles.length === 0 ? (
          <div data-testid="vehicles-empty" className="mt-10 rounded-2xl border border-dashed border-white/15 p-14 text-center">
            <Car className="w-10 h-10 text-slate-600 mx-auto" />
            <p className="mt-4 text-slate-400">Henüz araç eklemediniz.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-5">
            {sortedVehicles.map((v) => (
              <VehicleCard key={v.id} v={v} records={sortedHistories[v.id] || []}
                expanded={expanded === v.id}
                onToggle={() => setExpanded(expanded === v.id ? null : v.id)}
                onEdit={() => setVehicleForm({ mode: "edit", id: v.id, data: { vin: v.vin, plate: v.plate, brand: v.brand, model: v.model, year: v.year, km: v.km, engine: v.engine, fuel: v.fuel, transmission: v.transmission, color: v.color, body_type: v.body_type || "", trim: v.trim || "", vehicle_type: v.vehicle_type || "Otomobil", photo: v.photo || "", belgeler: belgeTarih(v.belgeler), photos: v.photos || [] } })}
                onTransfer={() => { setTransferFor(v); setTransferEmail(""); }}
                onPhotoChange={changePhoto}
                onDelete={() => deleteVehicle(v)}
                onAddRecord={() => setRecordForm({ vehicleId: v.id, record: null, data: { ...EMPTY_RECORD } })}
                onEditRecord={(r) => setRecordForm({ vehicleId: v.id, record: r, data: { type: r.type, date: r.date, title: r.title, description: r.description || "", km: r.km ?? "", cost: r.cost ?? "", severity: r.severity || "low" } })}
                onDeleteRecord={(rid) => deleteRecord(v.id, rid)} />
            ))}
          </div>
        )}

        {vehicleForm && (
          <VehicleFormModal vehicleForm={vehicleForm} setVehicleForm={setVehicleForm}
            uploading={uploading} fileRef={fileRef} onUploadPhotos={uploadPhotos}
            onSubmit={submitVehicle} onClose={() => setVehicleForm(null)} />
        )}

        {recordForm && (
          <GarageRecordModal recordForm={recordForm} setRecordForm={setRecordForm}
            onSubmit={submitRecord} onClose={() => setRecordForm(null)} />
        )}

        {transferFor && (
          <TransferModal vehicle={transferFor} email={transferEmail} setEmail={setTransferEmail}
            onSubmit={submitTransfer} onClose={() => setTransferFor(null)} />
        )}
      </div>
    </div>
  );
}
